<script setup lang="ts">
import './assets/style.scss'
import { useGlobalSearch } from '../model/model.ts'
import { isSearchResultElement, useGlobalSearchNavigation } from '../model/helpers.ts'
import { VIcons, VModal } from '@/shared/ui'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { debounce } from 'lodash'
import type { GlobalSearchType } from '../model/type.ts'
import { useRoute, useRouter } from 'vue-router'
import { WObjectSubTypeSelect } from '@/widgets'
import { DateTime } from 'ts-luxon'
import WGlobalSearchCoords from './WGlobalSearchCoords.vue'

const route = useRoute()
const router = useRouter()
const model = useGlobalSearch()
const { navigateTo, navigateToMap } = useGlobalSearchNavigation()

const locSearch = ref('')
const field = ref()
const activeItemId = ref<number>(0)

/**
 * Обновляет поисковый запрос после debounce и очищает выдачу для короткой строки.
 */
const updateSearchQuery = (value: string) => {
  const search = value.trim()

  if (search.length < 2) {
    model.query.q = ''
    model.clearResult()
    return
  }

  model.query.q = search
}

const debouncedSearch = debounce(updateSearchQuery, 500)

watch(locSearch, (value) => {
  if (!model.open) return
  debouncedSearch(value)
})

/**
 * Сбрасывает локальное и глобальное состояние быстрого поиска при закрытии модалки.
 */
const resetQuickSearch = () => {
  debouncedSearch.cancel()
  locSearch.value = ''
  model.query.q = ''
  model.query.object_type_id = 0
  model.query.filter = []
  model.query.area = null
  model.clearResult()
  activeItemId.value = 0
}

/** Очищаем данные при закрытии **/
watch(
  () => model.open,
  async () => {
    await nextTick()

    if (!model.open) {
      resetQuickSearch()
    } else {
      if (field.value) field.value.focus()
    }
  },
)

/** Выбираем первый элемент в выдаче */
watch(
  () => model.result,
  (results) => {
    if (results.length > 0) {
      activeItemId.value = results[0].id
    }
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('keydown', keyDownHandler)
  model.query.date = DateTime.now().toISODate() as string
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', keyDownHandler)
  debouncedSearch.cancel()
})

/**
 * Функция обработки нажатия кнопок
 **/
const keyDownHandler = (event: KeyboardEvent) => {
  if (!model.open || event.defaultPrevented || event.isComposing) return

  if (event.key === 'Escape') {
    event.preventDefault()
    model.open = false
    return
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    arrowHandler('UP')
    return
  }

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    arrowHandler('DOWN')
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()

    if (event.shiftKey) {
      allResultToMap()
    } else {
      enterHandler(event.metaKey || event.ctrlKey)
    }

    return
  }
}

/**
 * Обрабатывает выбор активного элемента из списка результатов поиска.
 * Определяет текущий активный элемент по activeItemId и делегирует навигацию в navigateTo.
 *
 * @param isMap - если true, открывает объект на карте (только для type === 'elem')
 */
const enterHandler = (isMap: boolean = false) => {
  if (!model.result.length) return

  const item = model.result.find((i) => i.id === activeItemId.value) ?? model.result[0]

  navigateTo(item, isMap)
}

/**
 * Выбирает конкретный элемент мышью без зависимости от текущего activeItemId.
 *
 * @param item  - элемент результата поиска
 * @param isMap - если true, открывает объект на карте
 */
const selectItem = (item: GlobalSearchType, isMap: boolean = false) => {
  activeItemId.value = item.id
  navigateTo(item, isMap)
}

/**
 * Выбираем все найденные элементы на карте
 */
const allResultToMap = () => {
  if (!model.result.length) return

  navigateToMap(model.result)
}

/**
 * Функция обработки перемещения стрелками
 * @param direction
 */
const arrowHandler = (direction: 'UP' | 'DOWN') => {
  if (!activeItemId.value || !model.result.length) return

  const currentIndex = model.result.findIndex((item) => item.id === activeItemId.value)
  const safeIndex = currentIndex === -1 ? 0 : currentIndex
  const offset = direction === 'UP' ? -1 : 1
  const nextIndex = (safeIndex + offset + model.result.length) % model.result.length

  activeItemId.value = model.result[nextIndex].id
}

/**
 * Функция переключения при помощи tab
 * @param item
 */
const focusHandler = (item: GlobalSearchType) => {
  activeItemId.value = item.id
}

/**
 * Вычисляем значение иконки платформы
 */
const isMac = computed(() => {
  const userAgent = window.navigator.userAgent.toLowerCase()
  return userAgent.includes('mac')
})

/**
 * Обрабатываем выбор типа объекта
 * @param type
 */
const objectTypeSelected = (type: any) => {
  model.objectTypeFields = type?.fields || []
}

/**
 * Переход на расширенный поиск
 */
const advanceSearchHandler = () => {
  router.push({
    name: 'global_search',
    params: { ...route.params, tab: 'search' },
    query: {
      q: model.query.q,
      object_type_id: model.query.object_type_id,
      date: model.query.date,
      area: JSON.stringify(model.query.area),
    },
  })

  model.open = false
}
</script>

<template>
  <VModal v-model="model.open" :width="900">
    <template #title>
      <div class="w-global-search__head">
        <WObjectSubTypeSelect
          hide-details
          :label="$t('itf.filter')"
          empty-value
          compact
          filtered
          v-model="model.query.object_type_id"
          @selected="objectTypeSelected"
          @resolved="objectTypeSelected"
        />

        <VTextField
          v-model="locSearch"
          ref="field"
          :placeholder="$t('itf.search')"
          :loading="model.isFetching"
          hide-details
        >
          <template #prepend-inner>
            <VIcons type="search" />
          </template>
        </VTextField>

        <VTextField
          type="date"
          hide-details
          :label="$t('itf.position_from_date')"
          v-model="model.query.date"
        />

        <VTooltip location="top" :text="$t('itf.advanced_search')">
          <template #activator="{ props }">
            <VBtn v-bind="props" @click="advanceSearchHandler">
              <VIcons type="search_gear" />
            </VBtn>
          </template>
        </VTooltip>

        <VTooltip location="top" :text="$t('itf.map')">
          <template #activator="{ props }">
            <VBtn
              :disabled="!model.result.length"
              v-bind="props"
              @click="allResultToMap"
            >
              <VIcons type="location_on" />
            </VBtn>
          </template>
        </VTooltip>
      </div>
    </template>

    <WGlobalSearchCoords />

    <div v-if="model.result.length" class="w-global-search__items">
      <div
        class="w-global-search__item"
        v-for="item in model.result"
        :class="{ 'w-global-search__item_active': activeItemId === item.id }"
        :key="item.id"
        tabindex="0"
        @mouseenter="activeItemId = item.id"
        @click="selectItem(item)"
        @focus="focusHandler(item)"
      >
        <div class="w-global-search__item-name">
          {{ item.object_name }}

          <small>{{ item.object_type_name }}</small>
        </div>

        <VTooltip :text="$t('itf.map')" v-if="isSearchResultElement(item.type)" location="top">
          <template #activator="{ props }">
            <div
              v-bind="props"
              class="w-global-search__item-icon"
              @click.stop="selectItem(item, true)"
            >
              <VIcons type="location_on" />
            </div>
          </template>
        </VTooltip>
      </div>
    </div>

    <div v-else class="w-global-search__empty-result">
      {{ $t('itf.empty_search') }}
    </div>

    <div class="w-global-search__info">
      <div class="w-global-search__info-item">
        <span>Esc</span> - {{ $t('itf.close').toLowerCase() }}
      </div>

      <div class="w-global-search__info-item">
        <VIcons type="subdirectory_arrow_left" /> - {{ $t('itf.select').toLowerCase() }}
      </div>

      <div class="w-global-search__info-item">
        <span v-if="!isMac">Ctrl</span> <VIcons v-else type="keyboard_command_key" /> +
        <VIcons type="subdirectory_arrow_left" /> -
        {{ $t('itf.map').toLowerCase() }}
      </div>

      <div class="w-global-search__info-item">
        <VIcons type="shift" /> + <VIcons type="subdirectory_arrow_left" /> -
        {{ $t('itf.map').toLowerCase() }}
      </div>

      <div class="w-global-search__info-item">
        <VIcons type="arrow_upward" /> <VIcons type="arrow_downward" /> -
        {{ $t('itf.navigation').toLowerCase() }}
      </div>
    </div>
  </VModal>
</template>
